import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { changeOpenDetalhesChamado, getVerChamado } from 'src/redux/slices/chamados';
import actionEditarChamado from 'src/actions/chamados/actionEditarChamado';
import useAuth from 'src/hooks/useAuth';
import Countdown from "react-countdown";
import Iconify from 'src/components/Iconify';
import { formatDistanceToNowStrict } from 'date-fns';
import getDateLocale from 'src/utils/getDateLocale';
import classNames from 'src/utils/classNames';
import { Badge } from 'src/@/components/ui/badge';
import { Avatar, AvatarFallback } from 'src/@/components/ui/avatar';

ChamadosCard.propTypes = {
  card: PropTypes.object,
  index: PropTypes.number,
  onDeleteTask: PropTypes.func,
  listaTags: PropTypes.array,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
};

const STATUS_MAP = {
  pendente: { label: 'Pendente', bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-800 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-500/30', dot: 'bg-amber-500' },
  aberto: { label: 'Aberto', bg: 'bg-blue-100 dark:bg-blue-500/15', text: 'text-blue-800 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-500/30', dot: 'bg-blue-500' },
  recusado: { label: 'Recusado', bg: 'bg-red-100 dark:bg-red-500/15', text: 'text-red-800 dark:text-red-400', border: 'border-red-200 dark:border-red-500/30', dot: 'bg-red-500' },
  concluido: { label: 'Concluido', bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-800 dark:text-green-400', border: 'border-green-200 dark:border-green-500/30', dot: 'bg-green-500' },
  cancelado: { label: 'Cancelado', bg: 'bg-gray-100 dark:bg-[#383838]', text: 'text-gray-600 dark:text-[#ABABAD]', border: 'border-gray-200 dark:border-[#565856]', dot: 'bg-gray-400' },
};

const PRIORITY_MAP = {
  imediato: { label: 'Imediato', bg: 'bg-red-500', text: 'text-white', icon: 'solar:danger-circle-bold' },
  alta: { label: 'Alta', bg: 'bg-orange-500', text: 'text-white', icon: 'mdi:arrow-up-bold' },
  normal: { label: 'Normal', bg: 'bg-[#7C3AED]', text: 'text-white', icon: 'mdi:minus' },
  baixa: { label: 'Baixa', bg: 'bg-blue-400', text: 'text-white', icon: 'mdi:arrow-down-bold' },
};

const AVATAR_COLORS = [
  'bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600',
  'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600',
];

function getInitials(name) {
  if (!name) return '?';
  const names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();
  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
}

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function ChamadosCard({ listaTags, card, onDeleteTask, onClick, selected, index }) {
  const dispatch = useDispatch();
  const { user } = useAuth();

  if (!card) return null;

  const { titulo, prioridade, data_criacao, nome_autor, criado_por, copiado_para, chamado: numeroChamado, ultima_modificacao, completado, status, data_para_conclusao, tipo } = card;

  const isMe = user.id === criado_por;
  const validaCopia = copiado_para ? copiado_para.includes(user.id) : false;
  const isLate = (status === "pendente" || status === "aberto") && data_para_conclusao && new Date() > new Date(data_para_conclusao);
  const statusCfg = STATUS_MAP[status] || STATUS_MAP.pendente;
  const priorityCfg = PRIORITY_MAP[prioridade] || PRIORITY_MAP.normal;

  async function handleOpenDetails() {
    if (status === 'pendente' && user.id !== criado_por && !isMe) {
      await actionEditarChamado(card._id, {
        action: 'aceitar_chamado',
        status: 'aberto',
        usuarioId: user.id,
      });
    }
    dispatch(getVerChamado(card._id));
    dispatch(changeOpenDetalhesChamado(true));
  }

  const countdownRenderer = ({ days, hours, minutes, seconds, completed: done }) => {
    if (done) return <span className="text-red-600 dark:text-red-400 font-bold text-[10px]">Atrasado</span>;
    const display = days > 0 ? `${days}d ${hours}h` : `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    return <span className="text-[10px]">{display}</span>;
  };

  return (
    <div
      className={classNames(
        'group relative cursor-pointer select-none transition-all duration-200 outline-none rounded-lg border p-3',
        selected
          ? 'bg-[#7C3AED]/5 dark:bg-[#7C3AED]/10 border-[#7C3AED]/40 ring-1 ring-[#7C3AED]/20'
          : isLate
            ? 'bg-red-50/60 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 hover:border-red-300 dark:hover:border-red-500/40 hover:bg-red-50 dark:hover:bg-red-500/15'
            : 'bg-slate-50 dark:bg-[#222529] border-slate-200 dark:border-[#383838] hover:border-slate-300 dark:hover:border-[#565856] hover:bg-white dark:hover:bg-[#2a2d32]'
      )}
      onClick={onClick ? () => onClick(card) : handleOpenDetails}
    >
      {/* Tipo badge */}
      <div className="mb-2">
        {tipo === 'externo' ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30">
            <Iconify icon="mdi:earth" width={11} />
            Externo
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30">
            <Iconify icon="mdi:account-group" width={11} />
            Interno
          </span>
        )}
      </div>

      {/* Top row: number + time */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] font-bold text-[#7C3AED] dark:text-violet-400">#{numeroChamado}</span>
          {isMe && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#7C3AED]/10 dark:bg-violet-500/20 text-[#7C3AED] dark:text-violet-400 uppercase tracking-wide">
              Meu
            </span>
          )}
          {validaCopia && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-medium border border-gray-200 dark:border-[#383838] text-gray-500 dark:text-[#ABABAD] uppercase tracking-wide">
              Copia
            </span>
          )}
        </div>
        <span className="text-[10px] text-gray-400 dark:text-[#ABABAD]">
          {(ultima_modificacao || data_criacao) &&
            formatDistanceToNowStrict(new Date(ultima_modificacao || data_criacao), { locale: getDateLocale() })}
        </span>
      </div>

      {/* Title */}
      <p className={classNames(
        "text-[13px] font-semibold text-gray-900 dark:text-[#D1D2D3] leading-snug mb-2.5 line-clamp-2",
        completado ? 'opacity-50 line-through decoration-gray-400 dark:decoration-[#565856]' : ''
      )}>
        {titulo}
      </p>

      {/* Badges row: status + priority */}
      <div className="flex items-center gap-1.5 mb-2.5">
        <Badge variant="outline" className={`text-[10px] font-semibold px-1.5 py-0 h-5 flex items-center gap-1 ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
          {statusCfg.label}
        </Badge>

        <div className={`flex items-center gap-1 px-1.5 h-5 rounded-md text-[10px] font-semibold ${priorityCfg.bg} ${priorityCfg.text}`}>
          <Iconify icon={priorityCfg.icon} width={10} />
          {priorityCfg.label}
        </div>

        {isLate && (
          <div className="flex items-center gap-1 px-1.5 h-5 rounded-md bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400 text-[10px] font-bold">
            <Iconify icon="mdi:alert" width={11} />
            Atrasado
          </div>
        )}
      </div>

      {/* Bottom row: author + countdown */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-[#383838]">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="w-5 h-5 flex-shrink-0">
            <AvatarFallback className={`${getAvatarColor(nome_autor)} text-white text-[8px] font-bold`}>
              {getInitials(nome_autor)}
            </AvatarFallback>
          </Avatar>
          <span className="text-[11px] text-gray-600 dark:text-[#ABABAD] truncate">{nome_autor}</span>
        </div>

        {data_para_conclusao && (status === "pendente" || status === "aberto") && !isLate && (
          <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10 px-1.5 py-0.5 rounded">
            <Iconify icon="mdi:clock-outline" width={11} />
            <Countdown renderer={countdownRenderer} date={data_para_conclusao} />
          </div>
        )}
      </div>
    </div>
  );
}
